import React, { useEffect, useMemo, useRef, useState } from "react";
import { Cancel } from "@mui/icons-material";
import { MembershipSchema } from "../data/types";
import {
  getMembershipDisplayName,
  membershipMatchesSearch,
  getMemberStatusColor,
} from "../utils/helpers";
import UserAvatar from "./UserAvatar";


export type TeamMemberInviteDetails = {
  first_name: string;
  last_name: string;
  email: string;
  username?: string;
  avatar?: string;
  uid?: string;
};

export const DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE =
  "A team member with this email address is already on this booking.";

export const INVITE_TEAM_MEMBER_SUBMIT_LABEL = "Add Team Member";
export const INVITE_SUCCESS_AUTO_CLOSE_MS = 100;

type AddMode = "lookup" | "manual";

type BookingInviteTeamMemberModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (member: TeamMemberInviteDetails) => void;
  existingMemberEmails?: string[];
  lookupMemberships?: MembershipSchema[];
};

type InviteTeamMemberFormProps = {
  onCancel: () => void;
  onInvite: (member: TeamMemberInviteDetails) => void;
  existingMemberEmails?: string[];
};

const InviteTeamMemberForm: React.FC<InviteTeamMemberFormProps> = ({
  onCancel,
  onInvite,
  existingMemberEmails = [],
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const isValid = Boolean(firstName.trim() && lastName.trim() && email.trim());
  const existingEmailSet = new Set(
    existingMemberEmails.map((value) => value.trim().toLowerCase())
  );

  const handleInvite = () => {
    if (!isValid) return;

    const normalizedEmail = email.trim().toLowerCase();
    if (existingEmailSet.has(normalizedEmail)) {
      setEmailError(DUPLICATE_TEAM_MEMBER_EMAIL_MESSAGE);
      return;
    }

    setEmailError(null);
    onInvite({
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
    });
  };

  return (
    <div className="flex flex-col w-full h-full text-left mx-auto sm:w-11/12">
      <p className="font-open-sans text-[14px] text-[#808080]">
        Fill in the details below to add a team member to this booking
      </p>

      <div className="flex flex-col gap-4 mt-4">
        <label className="flex flex-col gap-1">
          <span className="font-open-sans text-[14px] text-black">First Name</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Enter first name"
            className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#333333] font-open-sans p-3"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-open-sans text-[14px] text-black">Last Name</span>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Enter last name"
            className="border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#333333] font-open-sans p-3"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-open-sans text-[14px] text-black">Email Address</span>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) {
                setEmailError(null);
              }
            }}
            placeholder="Enter email address"
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? "invite-email-error" : undefined}
            className={`border-[1px] rounded-lg text-[16px] text-[#333333] font-open-sans p-3 ${
              emailError ? "border-red-500" : "border-[#E9E9E9]"
            }`}
          />
          {emailError ? (
            <p
              id="invite-email-error"
              data-testid="invite-email-error"
              className="font-open-sans text-[13px] text-red-600 mt-1"
            >
              {emailError}
            </p>
          ) : null}
        </label>
      </div>

      <div className="flex items-center justify-end gap-3 mt-8">
        <button
          type="button"
          onClick={onCancel}
          className="border-[1px] border-[#E9E9E9] bg-white text-[#333333] font-raleway font-semibold text-[16px] px-6 py-2 rounded-lg"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleInvite}
          disabled={!isValid}
          className={`border-0 font-raleway font-medium text-[16px] px-6 py-2 rounded-lg ${
            isValid
              ? "bg-primary-200 text-white hover:bg-back-300"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          }`}
        >
          {INVITE_TEAM_MEMBER_SUBMIT_LABEL}
        </button>
      </div>
    </div>
  );
};

type MembershipLookupViewProps = {
  lookupMemberships: MembershipSchema[];
  existingMemberEmails: string[];
  onCancel: () => void;
  onSelect: (member: TeamMemberInviteDetails) => void;
};

const MembershipLookupView: React.FC<MembershipLookupViewProps> = ({
  lookupMemberships,
  existingMemberEmails,
  onCancel,
  onSelect,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const existingEmailSet = useMemo(
    () => new Set(existingMemberEmails.map((value) => value.trim().toLowerCase())),
    [existingMemberEmails]
  );

  const availableMembers = useMemo(
    () =>
      lookupMemberships.filter((member) => {
        const email = member.email?.trim().toLowerCase();
        return email && !existingEmailSet.has(email) && membershipMatchesSearch(member, searchQuery);
      }),
    [lookupMemberships, existingEmailSet, searchQuery]
  );

  const handleSelect = (member: MembershipSchema) => {
    const email = member.email?.trim();
    if (!email) return;

    onSelect({
      first_name: member.first_name?.trim() || "",
      last_name: member.last_name?.trim() || "",
      email,
      username: member.username,
      avatar: member.avatar,
      uid: member.uid,
    });
  };

  return (
    <div className="flex flex-col w-full h-full text-left mx-auto sm:w-11/12">
      <p className="font-open-sans text-[14px] text-[#808080]">
        Search existing members or membership requests
      </p>

      <input
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search by name, email, or username"
        className="mt-4 border-[1px] border-[#E9E9E9] rounded-lg text-[16px] text-[#333333] font-open-sans p-3 w-full"
        data-testid="team-member-lookup-search"
      />

      <div
        className="mt-3 border-[1px] border-[#E9E9E9] rounded-lg bg-white max-h-[min(18rem,calc(100vh-16rem))] overflow-y-auto"
        data-testid="team-member-lookup-results"
      >
        {availableMembers.length > 0 ? (
          availableMembers.map((member, index) => {
            const key = member.uid || member.email || `lookup-member-${index}`;
            const displayName = getMembershipDisplayName(member)
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleSelect(member)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left border-0 bg-white hover:bg-[#FAFAFA] border-b border-[#F0F0F0] last:border-b-0"
              >
                <UserAvatar
                  firstName={member.first_name}
                  lastName={member.last_name}
                  username={member.username}
                  email={member.email}
                  avatar={member.avatar}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-open-sans text-[15px] text-[#333333] truncate">
                    {displayName}
                  </p>
                  <p className="font-open-sans text-[13px] text-[#808080] truncate">
                    {/* {member.email} */}
                    {(member?.username && displayName !== member?.username) ? `${member.username}` : ""}
                  </p>
                </div>

                {member?.status && getMemberStatusColor(member.status).bgColor && (
                  <span className={`text-[11px] font-raleway font-semibold uppercase tracking-wide text-[${getMemberStatusColor(member.status).primaryTextColor}] bg-[${getMemberStatusColor(member.status).bgColor}] px-2 py-1 rounded`}>
                    {member.status}
                  </span>
                )}
              </button>
            );
          })
        ) : (
          <p className="px-4 py-6 text-center font-open-sans text-[14px] text-[#808080]">
            {searchQuery.trim()
              ? "No matching members found"
              : "No available members to add"}
          </p>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="border-[1px] border-[#E9E9E9] bg-white text-[#333333] font-raleway font-semibold text-[16px] px-6 py-2 rounded-lg"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const BookingInviteTeamMemberModal: React.FC<BookingInviteTeamMemberModalProps> = ({
  isOpen,
  onClose,
  onInvite,
  existingMemberEmails = [],
  lookupMemberships = [],
}) => {
  const [formSessionKey, setFormSessionKey] = useState(0);
  const [addMode, setAddMode] = useState<AddMode>("lookup");
  const autoCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLookupMembers = lookupMemberships.length > 0;

  const clearAutoCloseTimeout = () => {
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
  };

  useEffect(() => () => clearAutoCloseTimeout(), []);

  useEffect(() => {
    if (isOpen) {
      setFormSessionKey((key) => key + 1);
      setAddMode(hasLookupMembers ? "lookup" : "manual");
    } else {
      clearAutoCloseTimeout();
    }
  }, [isOpen, hasLookupMembers]);

  const handleClose = () => {
    clearAutoCloseTimeout();
    onClose();
  };

  const handleInvite = (member: TeamMemberInviteDetails) => {
    onInvite(member);
    clearAutoCloseTimeout();
    autoCloseTimeoutRef.current = setTimeout(() => {
      autoCloseTimeoutRef.current = null;
      onClose();
    }, INVITE_SUCCESS_AUTO_CLOSE_MS);
  };

  return (
    <div
      className={`fixed inset-0 z-[100] ${isOpen ? "flex" : "hidden"} items-start justify-center p-4 sm:p-6`}
      data-testid="booking-invite-team-member-modal"
    >
      <div className="fixed inset-0 bg-white bg-opacity-90 z-[99]" onClick={handleClose} />
      <dialog
        className="z-[100] w-10/12 sm:w-5/12 max-w-lg h-auto bg-[#fff] relative rounded-2xl shadow-[#00000029] shadow-[0_0px_8px_rgba(0,0,0,0.16)] overflow-hidden flex flex-col top-16"
        open={isOpen}
        onClose={handleClose}
        style={{ margin: 0 }}
      >
        <div className="flex flex-row justify-end p-4 pb-0">
          <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
            <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
          </span>
        </div>
        <div className="flex flex-col w-full m-auto px-4 pb-6">
          <p className="font-raleway text-[16px] font-semibold text-black mb-4">Add team member</p>

          {hasLookupMembers && (
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setAddMode("lookup")}
                className={`flex-1 py-2 px-3 rounded-lg text-[14px] font-raleway font-medium border ${
                  addMode === "lookup"
                    ? "bg-primary-200/80 text-white border-primary-200/80"
                    : "bg-white text-[#4D4D4D] border-[#E9E9E9]"
                }`}
              >
                Search members
              </button>
              <button
                type="button"
                onClick={() => setAddMode("manual")}
                className={`flex-1 py-2 px-3 rounded-lg text-[14px] font-raleway font-medium border ${
                  addMode === "manual"
                    ? "bg-primary-200/80 text-white border-primary-200/80"
                    : "bg-white text-[#4D4D4D] border-[#E9E9E9]"
                }`}
              >
                Add manually
              </button>
            </div>
          )}

          {addMode === "lookup" && hasLookupMembers ? (
            <MembershipLookupView
              key={`lookup-${formSessionKey}`}
              lookupMemberships={lookupMemberships}
              existingMemberEmails={existingMemberEmails}
              onCancel={handleClose}
              onSelect={handleInvite}
            />
          ) : (
            <InviteTeamMemberForm
              key={formSessionKey}
              onCancel={handleClose}
              onInvite={handleInvite}
              existingMemberEmails={existingMemberEmails}
            />
          )}
        </div>
      </dialog>
    </div>
  );
};

export default BookingInviteTeamMemberModal;
